import type { FastifyReply, FastifyRequest } from 'fastify'
import { AdminService } from './admin.service.js'
import type { AuthUser } from '../../core/auth/auth0.js'
import { ZodError } from 'zod'
import {
  auditLogsQuerySchema,
  banUserSchema,
  changeUserPlanSchema,
  clearCacheSchema,
  deleteCacheEntryQuerySchema,
  listUsersQuerySchema,
  resolveFlagBodySchema,
  resolveFlagParamsSchema,
  suspendUserSchema,
  updateUserRolesSchema,
  updateUserStatusSchema,
  userIdParamsSchema
} from './admin.schemas.js'
import { ApiResponse } from '../../utils/response'

type AdminRequest = FastifyRequest & { user: AuthUser }

export class AdminController {
  private readonly service = new AdminService()

  async getStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const stats = await this.service.getDashboardStats()
      return reply.send(stats)
    } catch (error) {
      request.log.error(error, 'admin_get_stats_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch dashboard statistics')
    }
  }

  async getUsers(request: FastifyRequest, reply: FastifyReply) {
    try {
      const rawQuery = request.query ?? {}
      const parsedQuery = listUsersQuerySchema.safeParse(rawQuery)

      if (!parsedQuery.success) {
        request.log.warn(
          {
            validationErrors: parsedQuery.error.flatten()
          },
          'admin_get_users_query_validation_failed_falling_back'
        )
      }

      const query = parsedQuery.success ? parsedQuery.data : listUsersQuerySchema.parse({})
      const result = await this.service.listUsers(query)
      return reply.send(result)
    } catch (error) {
      request.log.error(error, 'admin_get_users_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid query parameters')
      }
      return ApiResponse.internalError(reply, 'Failed to fetch users')
    }
  }

  async getUser(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const user = await this.service.getUserDetails(params.userId)
      if (!user) {
        return ApiResponse.notFound(reply, 'User not found')
      }
      return reply.send(user)
    } catch (error) {
      request.log.error(error, 'admin_get_user_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid user id')
      }
      return ApiResponse.internalError(reply, 'Failed to fetch user')
    }
  }

  async updateUserStatus(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const body = updateUserStatusSchema.parse(request.body ?? {})

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.updateUserStatus(params.userId, body.status, actor, undefined, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_update_user_status_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid payload')
      }
      return ApiResponse.internalError(reply, 'Failed to update user status')
    }
  }

  async updateUserRoles(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const body = updateUserRolesSchema.parse(request.body ?? {})

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.updateUserRoles(params.userId, body.roles, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_update_user_roles_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid roles payload')
      }
      return ApiResponse.internalError(reply, 'Failed to update user roles')
    }
  }

  async changeUserPlan(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const body = changeUserPlanSchema.parse(request.body ?? {})

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.changeUserPlan(params.userId, body.plan, actor, request.ip as string, body.subscriptionStatus ?? null)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_change_user_plan_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid plan payload')
      }
      return ApiResponse.internalError(reply, 'Failed to change user plan')
    }
  }

  async resetUserQuota(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.resetUserQuota(params.userId, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_reset_user_quota_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid user id')
      }
      return ApiResponse.internalError(reply, 'Failed to reset user quota')
    }
  }

  async suspendUser(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const body = suspendUserSchema.parse(request.body ?? {})
      const normalizedReason = body.reason && body.reason.length > 0 ? body.reason : undefined

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.suspendUser(params.userId, body.durationDays, normalizedReason, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_suspend_user_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid suspension payload')
      }
      return ApiResponse.internalError(reply, 'Failed to suspend user')
    }
  }

  async banUser(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const body = banUserSchema.parse(request.body ?? {})
      const normalizedReason = body.reason && body.reason.length > 0 ? body.reason : undefined
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.banUser(params.userId, normalizedReason, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_ban_user_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid ban payload')
      }
      return ApiResponse.internalError(reply, 'Failed to ban user')
    }
  }

  async resetUserTokens(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) return ApiResponse.unauthorized(reply)
      await this.service.resetUserTokens(params.userId, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_reset_user_tokens_failed')
      return ApiResponse.internalError(reply, 'Failed to reset tokens')
    }
  }

  async purgeUserCache(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) return ApiResponse.unauthorized(reply)
      await this.service.purgeUserCache(params.userId, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_purge_user_cache_failed')
      return ApiResponse.internalError(reply, 'Failed to purge user cache')
    }
  }

  async resendOnboarding(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) return ApiResponse.unauthorized(reply)
      await this.service.resendOnboarding(params.userId, actor, request.ip as string)
      return reply.send({ success: true })
    } catch (error) {
      request.log.error(error, 'admin_resend_onboarding_failed')
      return ApiResponse.internalError(reply, 'Failed to resend onboarding email')
    }
  }

  async getUserDiagnostic(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})
      const data = await this.service.getUserDiagnostic(params.userId)
      return reply.send(data)
    } catch (error) {
      request.log.error(error, 'admin_get_user_diagnostic_failed')
      return ApiResponse.internalError(reply, 'Failed to generate diagnostic')
    }
  }

  async getModerationQueue(request: FastifyRequest, reply: FastifyReply) {
    try {
      const flags = await this.service.getModerationFlags()
      return reply.send(flags)
    } catch (error) {
      request.log.error(error, 'admin_get_moderation_queue_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch moderation queue')
    }
  }

  async getModerationStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const stats = await this.service.getModerationStats()
      return reply.send(stats)
    } catch (error) {
      request.log.error(error, 'admin_get_moderation_stats_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch moderation statistics')
    }
  }

  async resolveFlag(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = resolveFlagParamsSchema.parse(request.params ?? {})
      const body = resolveFlagBodySchema.parse(request.body ?? {})
      const resolutionNote = body.note && body.note.length > 0 ? body.note : undefined
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      const resolution = await this.service.resolveFlag(
        params.flagId,
        body.action,
        actor,
        {
          resolutionNote,
          suspendDays: body.action === 'suspend' ? body.suspendDays ?? 7 : body.suspendDays
        },
        request.ip as string
      )

      if (!resolution) {
        return ApiResponse.notFound(reply, 'Flag not found')
      }

      return reply.send({ success: true, flag: resolution })
    } catch (error) {
      request.log.error(error, 'admin_resolve_flag_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid moderation payload')
      }
      return ApiResponse.internalError(reply, 'Failed to resolve flag')
    }
  }

  async getCacheStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const stats = await this.service.getCacheStats()
      return reply.send(stats)
    } catch (error) {
      request.log.error(error, 'admin_get_cache_stats_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch cache stats')
    }
  }

  async clearCache(request: FastifyRequest, reply: FastifyReply) {
    try {
      const body = clearCacheSchema.parse(request.body ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      const removed = await this.service.clearCache(body.namespace, actor, request.ip as string | undefined)
      return reply.send({ success: true, removed })
    } catch (error) {
      request.log.error(error, 'admin_clear_cache_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid cache namespace')
      }
      return ApiResponse.internalError(reply, 'Failed to clear cache')
    }
  }

  async sweepCache(request: FastifyRequest, reply: FastifyReply) {
    try {
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      const removed = await this.service.sweepCache(actor, request.ip as string)
      return reply.send({ success: true, removed })
    } catch (error) {
      request.log.error(error, 'admin_sweep_cache_failed')
      return ApiResponse.internalError(reply, 'Failed to sweep cache')
    }
  }

  async deleteCacheEntry(request: FastifyRequest, reply: FastifyReply) {
    try {
      const query = deleteCacheEntryQuerySchema.parse(request.query ?? {})
      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      const removed = await this.service.deleteCacheEntry(query.key, actor, request.ip as string)
      return reply.send({ success: removed })
    } catch (error) {
      request.log.error(error, 'admin_delete_cache_entry_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid cache key')
      }
      return ApiResponse.internalError(reply, 'Failed to delete cache entry')
    }
  }



  async getAuditLogs(request: FastifyRequest, reply: FastifyReply) {
    try {
      const { limit } = auditLogsQuerySchema.parse(request.query ?? {})
      const logs = await this.service.getAuditLogs(limit)
      return reply.send({ logs })
    } catch (error) {
      request.log.error(error, 'admin_get_audit_logs_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid pagination payload')
      }
      return ApiResponse.internalError(reply, 'Failed to fetch audit logs')
    }
  }

  async getSystemHealth(request: FastifyRequest, reply: FastifyReply) {
    try {
      const health = await this.service.getSystemHealth()
      return reply.send(health)
    } catch (error) {
      request.log.error(error, 'admin_get_system_health_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch system health')
    }
  }

  async getHealthHistory(request: FastifyRequest, reply: FastifyReply) {
    try {
      const query = request.query as { hours?: string }
      const hours = Number(query?.hours) || 24
      const history = await this.service.getHealthHistory(hours)
      return reply.send(history)
    } catch (error) {
      request.log.error(error, 'admin_get_health_history_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch health history')
    }
  }

  async getHealthStats(request: FastifyRequest, reply: FastifyReply) {
    try {
      const query = request.query as { hours?: string }
      const hours = Number(query?.hours) || 24
      const stats = await this.service.getHealthStats(hours)
      return reply.send(stats)
    } catch (error) {
      request.log.error(error, 'admin_get_health_stats_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch health stats')
    }
  }

  async getHealthSuggestions(request: FastifyRequest, reply: FastifyReply) {
    try {
      const suggestions = await this.service.getHealthSuggestions()
      return reply.send(suggestions)
    } catch (error) {
      request.log.error(error, 'admin_get_health_suggestions_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch health suggestions')
    }
  }

  async getHealthTrends(request: FastifyRequest, reply: FastifyReply) {
    try {
      const query = request.query as { days?: string }
      const days = Number(query?.days) || 7
      const trends = await this.service.getHealthTrends(days)
      return reply.send(trends)
    } catch (error) {
      request.log.error(error, 'admin_get_health_trends_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch health trends')
    }
  }

  async getServiceComparison(request: FastifyRequest, reply: FastifyReply) {
    try {
      const comparison = await this.service.getServiceComparison()
      return reply.send(comparison)
    } catch (error) {
      request.log.error(error, 'admin_get_service_comparison_failed')
      return ApiResponse.internalError(reply, 'Failed to fetch service comparison')
    }
  }

  async getSecurityAlerts(_request: FastifyRequest, reply: FastifyReply) {
    try {
      const alerts = await this.service.getSecurityAlerts()
      return reply.send({ alerts })
    } catch (error) {
      return ApiResponse.internalError(reply, 'Failed to fetch security alerts')
    }
  }

  /**
   * Activate a user account (allow access to the app in production)
   * POST /api/admin/users/:userId/activate
   */
  async activateUser(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.setUserActivation(params.userId, true, actor, request.ip as string)
      return reply.send({ success: true, activated: true })
    } catch (error) {
      request.log.error(error, 'admin_activate_user_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid user id')
      }
      return ApiResponse.internalError(reply, 'Failed to activate user')
    }
  }

  /**
   * Deactivate a user account (revoke access in production)
   * POST /api/admin/users/:userId/deactivate
   */
  async deactivateUser(request: FastifyRequest, reply: FastifyReply) {
    try {
      const params = userIdParamsSchema.parse(request.params ?? {})

      const actor = (request as AdminRequest).user
      if (!actor) {
        return ApiResponse.unauthorized(reply)
      }

      await this.service.setUserActivation(params.userId, false, actor, request.ip as string)
      return reply.send({ success: true, activated: false })
    } catch (error) {
      request.log.error(error, 'admin_deactivate_user_failed')
      if (error instanceof ZodError) {
        return ApiResponse.badRequest(reply, 'Invalid user id')
      }
      return ApiResponse.internalError(reply, 'Failed to deactivate user')
    }
  }
}
